// WP-4.7 (OB-1, S-41 area, "AI (…, activity log link)"): the full OB-1 viewer — input ->
// interpretation -> confidence -> mode -> resulting records, for EVERY AI-touched capture,
// not just what's still actionable (Inbox's own §S-07 scope, which windows auto-recorded
// items to the last 24h). capture_item already carries every OB-1 field (WP-2.5 onward) — this
// page reuses the SAME /api/v1/inbox data Inbox.tsx fetches, just presented as one flat,
// never-windowed, reverse-chronological log instead of grouped by actionable status.
import { formatMoney, type CurrencyCode } from '@pfos/engine';
import type { CaptureItemResponse, CaptureMode } from '@pfos/shared';
import { History } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState } from '../../components/EmptyState';
import { selectClass } from '../../components/FormKit';
import { useInbox } from '../../lib/inbox';

const SOURCE_LABEL: Record<string, string> = { whatsapp: 'WhatsApp', web: 'Web', import: 'Import' };
const STATUS_LABEL: Record<string, string> = {
  needs_one_tap: 'Needs one-tap',
  needs_question: 'Needs question',
  auto_recorded: 'Recorded',
  unparsed_failed: 'Unparsed',
  duplicate_suspect: 'Duplicate suspect',
};
const MODE_LABEL: Record<CaptureMode, string> = {
  A: 'Mode A — auto-record',
  B: 'Mode B — confirm',
  C: 'Mode C — question',
};

/** The SAME per-item summary Inbox.tsx's own `summaryLine` builds, plus the party field it
 * omits — this page is an audit trail, not an action queue, so a little more detail earns
 * its place here. */
function interpretationLine(item: CaptureItemResponse): string {
  if (item.extractions !== null) return `${String(item.extractions.length)} linked events`;
  const ex = item.extraction;
  if (ex === null) return item.unparsedReason ?? '—';
  const bits: string[] = [];
  if (ex.amountMinor !== null && ex.currency !== null) {
    bits.push(formatMoney({ currency: ex.currency as CurrencyCode, minorUnits: ex.amountMinor }));
  }
  if (ex.category !== null) bits.push(ex.category.label);
  if (ex.party !== null) bits.push(ex.party.label);
  if (ex.account !== null) bits.push(ex.account.label);
  return bits.length > 0 ? bits.join(' · ') : '—';
}

export function SettingsAiActivity(): React.JSX.Element {
  const { data, isLoading } = useInbox();
  const [modeFilter, setModeFilter] = useState<'all' | CaptureMode>('all');

  const items = useMemo(() => {
    return [...(data?.items ?? [])]
      .filter((item) => modeFilter === 'all' || item.mode === modeFilter)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [data, modeFilter]);

  if (isLoading) return <p className="text-sm text-muted">Loading…</p>;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-medium text-ink">AI activity log</h2>
          <p className="text-xs text-muted">
            Every AI-originated capture: input, interpretation, confidence, mode, and any resulting
            record (OB-1).
          </p>
        </div>
        <select
          aria-label="Filter by mode"
          className={`${selectClass} w-auto shrink-0`}
          value={modeFilter}
          onChange={(e) => {
            setModeFilter(e.target.value as 'all' | CaptureMode);
          }}
        >
          <option value="all">All modes</option>
          <option value="A">Mode A</option>
          <option value="B">Mode B</option>
          <option value="C">Mode C</option>
        </select>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={History} message="No AI-originated captures yet." />
      ) : (
        <ul className="divide-y divide-border rounded-[var(--radius-card)] border border-border bg-surface">
          {items.map((item) => (
            <li key={item.id} className="px-4 py-3 text-sm">
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className="text-xs text-muted">
                  {SOURCE_LABEL[item.source] ?? item.source} ·{' '}
                  {new Date(item.createdAt).toLocaleString()}
                </span>
                <span className="shrink-0 text-xs text-muted">
                  {STATUS_LABEL[item.status] ?? item.status}
                </span>
              </div>
              <p className="mb-1 truncate text-ink">
                {item.rawText ?? (item.kind === 'image' ? 'Receipt photo' : '(no text)')}
              </p>
              <p className="mb-1 text-xs text-muted">{interpretationLine(item)}</p>
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted">
                {item.confidence !== null && <span>confidence: {item.confidence}</span>}
                {item.mode !== null && <span>{MODE_LABEL[item.mode]}</span>}
                {item.resultEntityId !== null ? (
                  <Link
                    to={`/tx/${item.resultEntityId}`}
                    className="font-medium text-primary hover:underline"
                  >
                    View record
                  </Link>
                ) : (
                  <Link
                    to={`/inbox/${item.id}`}
                    className="font-medium text-primary hover:underline"
                  >
                    View item
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
